<template>
  <Layout>
    <template #header-container>
      <div class="mc-list-header">
        <PageHeader v-if="!isLoading" isSticky :title="title">
          <template v-if="isAutostartEnabled" #banner>
            <div v-if="hasAutoEndedQuickAssignment">
              <div
                v-if="automationBannerItems.length > 0"
                class="autostart-banner __success-automation"
              >
                <div class="autostart-banner__title">
                  <i class="icon-fill_flash" />
                  <span>{{
                    $t(`fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.title`)
                  }}</span>
                </div>

                <div class="autostart-banner__content">
                  <span
                    v-for="(item, index) in automationBannerItems"
                    :key="`${item.label}-${index}`"
                    class="autostart-banner__item"
                  >
                    <strong>{{ item.label }}:</strong>
                    <span>{{ item.value }}</span>
                  </span>
                </div>
              </div>

              <div
                v-if="errorAutostartBanner !== null && status === 'SCHEDULED'"
                class="autostart-banner __error"
              >
                <i class="icon-fill_slash" />
                <span>{{
                  $t(`fleetTaskInstant.banner.autostart.failed.${errorAutostartBanner}`)
                }}</span>
              </div>
            </div>
            <div v-else>
              <div
                v-if="isEnableAutostart && errorAutostartBanner === null"
                class="autostart-banner __success"
              >
                <i class="icon-fill_flash" />
                <span>{{ $t('fleetTaskInstant.banner.autostart.success') }}</span>
              </div>

              <div
                v-if="errorAutostartBanner !== null && status === 'SCHEDULED'"
                class="autostart-banner __error"
              >
                <i class="icon-fill_slash" />
                <span>{{
                  $t(`fleetTaskInstant.banner.autostart.failed.${errorAutostartBanner}`)
                }}</span>
              </div>
            </div>
          </template>
          <template v-if="isEditable" #besideTitle>
            <div class="d-flex align-items-center gap-1 autosave-wrapper">
              <template v-if="isLoadingGlobal && !isInitial && !isLoadingTransition">
                <div class="mc-fa icon-outline_Sync" />
                <div>{{ $t('fleetTaskInstant.text.autosave.saving') }}</div>
              </template>
              <template v-else>
                <div id="autosave-tooltip" class="position-relative">
                  <svg class="circle-animation" height="18" width="18">
                    <circle cx="9" cy="9" r="7" />
                  </svg>
                  <div class="mc-fa icon-fill_checkmark-circle" />
                </div>
                <div v-if="!isInitial" class="saved-text">
                  {{ $t('fleetTaskInstant.text.autosave.saved') }}
                </div>
              </template>
              <b-tooltip
                custom-class="autosave-tooltip-wrapper"
                target="autosave-tooltip"
                triggers="hover"
                placement="bottom"
              >
                <div>{{ $t('fleetTaskInstant.title.autosave') }}</div>
                <div class="subtitle">
                  {{ $t('fleetTaskInstant.subtitle.autosave', { time: showLastUpdated }) }}
                </div>
              </b-tooltip>
            </div>
          </template>
          <template #afterTitle>
            <div class="d-flex gap-2">
              <!-- Action Buttons in TEMPORARY status  -->
              <template v-if="status === 'TEMPORARY'">
                <McButton
                  variant="secondary"
                  icon="icon-outline_close"
                  :text="$t('global.action.cancel')"
                  :disabled="isLoadingGlobal"
                  @click="handleCancel(true, $event)"
                />
                <McButton
                  variant="secondary"
                  :text="$t('fleetTaskInstant.action.saveAsDraft')"
                  icon="icon-outline_save"
                  :disabled="isLoadingGlobal"
                  @click="handleTransitionStatus('DRAFT', false, $event)"
                />
              </template>

              <!-- Navigation Back Button -->
              <McButton
                v-else
                variant="secondary"
                icon="icon-outline_arrow-left"
                :text="$t('global.back')"
                :disabled="isLoadingGlobal"
                @click="handleBack"
              />

              <!-- Other Button for other status than 'TEMPORARY', 'CANCELED' -->
              <OtherOptions
                v-if="!['TEMPORARY'].includes(status)"
                :isLoading="isLoadingGlobal"
                :status="status"
                :actionHandlers="{
                  handleDuplicate,
                  handleChangeVehicleDriver,
                  handleExportPdf,
                  handleFleetTaskInstantHistory,
                  handleDelete: () => (isShowDeleteModal = true),
                  handleCancel: (e) => handleTransitionStatus('CANCELED', false, e),
                }"
              />

              <!-- Share Button for 'STARTED' & 'ENDED' status -->
              <ShareTrackButton
                v-if="['STARTED', 'ENDED'].includes(status)"
                :rawStatus="$store.state.fleetTaskInstant.showResponse.status?.raw_type"
                size="md"
                @click="shareTrackBtnHandler"
              />

              <!-- History Button for 'CANCELED' status -->
              <McButton
                v-if="status === 'CANCELED'"
                variant="secondary"
                icon="icon-outline_history"
                :text="$t('fleetTaskInstant.action.assignmentHistory')"
                :disabled="isLoadingGlobal"
                @click="handleFleetTaskInstantHistory"
              />

              <!-- Transition Action Buttons to 'SCHEDULED' status -->
              <McButton
                v-if="['TEMPORARY', 'DRAFT'].includes(status)"
                variant="secondary"
                :text="$t('fleetTaskInstant.action.scheduleAssignment')"
                icon="icon-outline_scheduler"
                :disabled="isLoadingGlobal"
                @click="handleTransitionStatus('SCHEDULED', false, $event)"
              />

              <!-- Transition Action Buttons to 'STARTED' status -->
              <McButton
                v-if="['TEMPORARY', 'DRAFT', 'SCHEDULED'].includes(status)"
                id="start-button"
                variant="primary"
                :text="$t('fleetTaskInstant.action.startAssignmentNow')"
                icon="icon-outline_truck-moving"
                :disabled="isLoadingGlobal"
                @click="handleTransitionStatus('STARTED')"
              />
              <!-- Autostart Tooltip -->
              <b-tooltip
                v-if="isEnableAutostart && isAutostartEnabled"
                custom-class="autostart-tooltip-wrapper"
                target="start-button"
                triggers="hover"
                placement="bottom"
              >
                {{ $t('fleetTaskInstant.tooltip.autostart.active') }}
              </b-tooltip>

              <!-- Finish FO Action Button and Modal -->
              <McButton
                v-if="status === 'STARTED'"
                variant="primary"
                icon="icon-outline_checkmark"
                :split="hasAutoEndedQuickAssignment"
                action-icon="icon-outline_settings"
                :text="$t('fleetTaskInstant.action.finishAssignment')"
                :disabled="isLoadingGlobal"
                @click="handleTransitionStatus('ENDED')"
                @action-click="handleSettingAction"
              />

              <template
                v-if="['TEMPORARY', 'DRAFT', 'SCHEDULED'].includes(status) && isAutostartEnabled"
              >
                <div class="__divider" />
                <McButton
                  v-show="!isEnableAutostart"
                  id="activate-autostart"
                  variant="success"
                  icon="icon-fill_flash"
                  :split="hasAutoEndedQuickAssignment"
                  action-icon="icon-outline_settings"
                  :text="hasAutoEndedQuickAssignment ? $t('fleetTaskInstant.action.activateAutomation') : $t('fleetTaskInstant.action.activateAutostart')"
                  :disabled="isLoadingGlobal"
                  @click="handleEnableAutostart"
                  @action-click="handleSettingAction"
                />
                <b-tooltip
                  custom-class="autostart-tooltip-wrapper"
                  target="activate-autostart"
                  triggers="hover"
                  placement="bottom"
                >
                  {{ $t('fleetTaskInstant.tooltip.autostart.activate') }}
                </b-tooltip>

                <McButton
                  v-show="isEnableAutostart && status === 'SCHEDULED'"
                  id="nonactivate-autostart"
                  variant="secondary"
                  :icon="hasAutoEndedQuickAssignment ? 'icon-fill_flash-off' : 'icon-fill_refresh'"
                  :split="hasAutoEndedQuickAssignment"
                  action-icon="icon-outline_settings"
                  :text="
                    hasAutoEndedQuickAssignment
                      ? $t('fleetTaskInstant.action.deactivateAutomation')
                      : $t('fleetTaskInstant.action.switchManualMode')
                  "
                  :disabled="isLoadingGlobal"
                  @click="handleDisableAutostart"
                  @action-click="handleSettingAction"
                />
                <b-tooltip
                  custom-class="autostart-tooltip-wrapper"
                  target="nonactivate-autostart"
                  triggers="hover"
                  placement="bottom"
                >
                  {{ $t('fleetTaskInstant.tooltip.autostart.nonactivate') }}
                </b-tooltip>
              </template>
            </div>
          </template>

          <template #footer>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton-img no-aspect height="72px" class="mc-wizard-skeleton mt-3 mb-3" />
              </template>
              <McWizard
                compact
                :items="wizardItems"
                :activeKey="status"
                class="mc-wizard mt-3 mb-3"
              />
            </b-skeleton-wrapper>
          </template>
        </PageHeader>
      </div>
    </template>

    <!-- Confirmation Modal -->
    <ConfirmModal
      :type="modalConfirmType"
      :status="status"
      :transition-status="pendingTransitionStatus"
      @transition="handleTransitionStatus"
      @cancel="handleCancelConfirm"
    />

    <div class="fleet-task-instant-detail__wrapper">
      <Tabs
        class="mc-delivery-tabs"
        :activeTab="activeTab"
        :isLoading="isLoading || isLoadingGlobal"
        @update:activeTab="activeTab = $event"
      />
      <b-skeleton-img v-if="isLoading" />
      <template v-else>
        <div v-show="activeTab == 'MAIN-INFORMATION'" class="task-detail-wrapper">
          <MapPreview
            :width="
              isEditable ? '40%' : `calc(100% - min(67%, ${406 * (isShowDetail ? 2 : 1)}px ))`
            "
          />
          <DetailSection v-if="isEditable" />
          <TaskDetailSection v-else />
        </div>
        <AdditionalInformation
          v-show="activeTab == 'ADDITIONAL-INFORMATION' && shouldShowAdditionalInformation"
        />
      </template>
    </div>

    <McEventList
      :value.sync="openHistory"
      :data="historyData"
      :keys="historyKeys"
      :title="$t('fleetTaskInstant.title.assignmentStatusHistory')"
    />

    <!-- Cancel in TEMPORARY status -->
    <McConfirmation
      v-model="isShowCancelModal"
      centered
      noCloseOnEsc
      noCloseOnBackdrop
      :title="$t('global.confirmation.title')"
      :ok-title="$t('global.confirmation.stayPage')"
      :cancel-title="$t('global.confirmation.exitPage')"
      @handle-ok="isShowCancelModal = false"
      @cancel="handleCancel(false)"
    >
      <div>
        <p>{{ $t('global.confirmation.question') }}</p>
        <p>{{ $t('global.confirmation.sure') }}</p>
      </div>
    </McConfirmation>

    <!-- Delete in DRAFT & SCHEDULED status -->
    <McConfirmation
      v-model="isShowDeleteModal"
      centered
      noCloseOnEsc
      noCloseOnBackdrop
      :title="$t('fleetTaskInstant.confirmModal.deleteConfirmation.title')"
      :ok-title="$t('fleetTaskInstant.confirmModal.deleteConfirmation.continueDelete')"
      :cancel-title="$t('fleetTaskInstant.confirmModal.deleteConfirmation.cancelDelete')"
      @handle-ok="handleDelete"
      @cancel="isShowDeleteModal = false"
    >
      <McAlert
        v-if="['ENDED', 'CANCELED'].includes(status)"
        withIcon
        variant="warning"
        class="mb-3"
      >
        <template #label>
          <p
            class="m-0"
            v-html="$t('fleetTaskInstant.confirmModal.deleteConfirmation.attentionNote')"
          />
        </template>
      </McAlert>
      <p
        v-html="
          $t('fleetTaskInstant.confirmModal.deleteConfirmation.areYouSure', {
            foNumber: showResponse.number,
          })
        "
      />
    </McConfirmation>

    <UpdateVehicleDriverModal ref="update-vehicle-driver-modal-ref" />

    <ShareTrackModal
      modalName="share-track-modal"
      :number="$store.state.fleetTaskInstant.showResponse.number"
      :trackLink="$store.state.fleetTaskInstant.showResponse.track_link"
    />

    <TransitionToCancelModal
      ref="mc-modal-canceled"
      :entityData="showResponse"
      :newStatusTransition="{ status: { name: status } }"
      :selectedItems="[showResponse]"
      @transition="handleTransitionStatus('CANCELED', true, null, $event)"
    />

    <ModalValueHelp
      ref="mc-modal-user-group"
      :context="userGroupContext"
      :attrs="userGroupAttrs"
      @update:fullObject="handleUserGroupSelect"
    />

    <AutomationConfigurationModal
      @save-automation-configuration="handleSaveAutomationConfiguration"
    />
  </Layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'

import McButton from '@/components/McButton'
import McWizard from '@/components/McWizard'
import McEventList from '@/components/McEventList'
import McConfirmation from '@/components/McConfirmation'
import McAlert from '@/components/McAlert'
import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

import DetailSection from './sections/DetailSection'
import TaskDetailSection from './sections/TaskDetailSection'
import MapPreview from './sections/MapPreview'
import AdditionalInformation from './sections/AdditionalInformation'

import ConfirmModal from './components/ConfirmModal'
import ShareTrackButton from './components/ShareTrackButton'
import ShareTrackModal from './components/ShareTrackModal'
import TransitionToCancelModal from './components/TransitionToCancelModal'
import OtherOptions from './components/OtherOptions'
import Tabs from './components/Tabs'
import AutomationConfigurationModal from './components/AutomationConfigurationModal'

import { wizardStatusItems } from './lib/constants/wizard-items.js'
import { successMsg, errorMsg } from './lib/constants/toast-msg.js'
import { EventBus } from './lib/event-bus.js'
import { redirectLiveview2 } from '@/helpers/navigation-guards'
import { formatIndexDate } from '@/helpers/tzDate'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import UpdateVehicleDriverModal from './components/UpdateVehicleDriverModal'

export default {
  name: 'FleetTaskInstantDetail',
  page: {
    title: 'Quick Assignment',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    PageHeader,
    Layout,
    McButton,
    McWizard,
    McEventList,
    McConfirmation,
    McAlert,
    DetailSection,
    TaskDetailSection,
    MapPreview,
    ConfirmModal,
    ShareTrackButton,
    ShareTrackModal,
    TransitionToCancelModal,
    OtherOptions,
    UpdateVehicleDriverModal,
    Tabs,
    AdditionalInformation,
    ModalValueHelp,
    AutomationConfigurationModal,
  },
  mixins: [Helper],
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('fleetTaskInstant/resetMap')
    this.$store.dispatch('fleetTaskInstant/resetSelectedPoints')
    next()
  },
  data() {
    return {
      pendingTransitionStatus: null,
      modalConfirmType: null,
      timeInterval: null,

      isLoading: true,
      isLoadingTransition: false,
      isShowCancelModal: false,
      isShowDeleteModal: false,
      openHistory: false,
      historyData: [],
      historyKeys: [],

      activeTab: 'MAIN-INFORMATION',

      isUserTyping: false,
      typingTimeout: null,

      userGroupContext: {
        label: 'User Group',
        model: {},
        blurHandler: () => {},
      },
      skipValidationUserGroup: false,
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'isLoadingGlobal',
      'isInitial',
      'showResponse',
      'errorResponse',
      'listPointRes',
      'selectedPointIdx',
    ]),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      if (!this.status) {
        return ''
      }

      return this.status === 'TEMPORARY'
        ? this.$t('fleetTaskInstant.title.create')
        : `${this.$t('fleetTaskInstant.title.list')} #${this.showResponse.number}`
    },
    status() {
      return this.showResponse?.status?.raw_type || 'TEMPORARY'
    },
    wizardStatusItems() {
      return wizardStatusItems(this)
    },
    wizardItems() {
      if (this.status === 'CANCELED') {
        return [
          this.wizardStatusItems.DRAFT,
          this.wizardStatusItems.SCHEDULED,
          this.wizardStatusItems.STARTED,
          this.wizardStatusItems.CANCELED,
        ]
      }

      return [
        this.wizardStatusItems.DRAFT,
        this.wizardStatusItems.SCHEDULED,
        this.wizardStatusItems.STARTED,
        this.wizardStatusItems.ENDED,
      ]
    },
    isEditable() {
      return ['TEMPORARY', 'DRAFT', 'SCHEDULED'].includes(this.status)
    },
    isShowDetail() {
      if (this.selectedPointIdx === null) {
        return false
      }

      return true
    },
    shouldShowAdditionalInformation() {
      return this.$store.state.identity.whoami?.accessRight !== 'QA On Demand'
    },
    showLastUpdated() {
      const lastUpdated = this.showResponse?.updated_on ?? this.showResponse?.created_on

      if (!lastUpdated) return null

      const formattedDate = this.formatIndexDate(lastUpdated)
      const isToday = formattedDate.date === moment().format('DD MMM YYYY')

      return isToday ? formattedDate.time : `${formattedDate.date}, ${formattedDate.time}`
    },
    isEnableAutostart() {
      return this.showResponse?.auto_start_type === 'GEOFENCE'
    },
    errorAutostartBanner() {
      if (this.errorResponse?.issues === undefined) {
        return null
      }

      const issue = this.errorResponse?.issues.find((issue) => {
        return issue.code === 'FLEET_TASK.FLEET_TASK_INSTANT.AUTO_START_FAILED_REASON'
      })

      if (!issue) {
        return null
      }

      return issue.message === 'DRIVER_CONFLICT' ? 'driver' : 'vehicle'
    },
    isAutostartEnabled() {
      return this.getFeatureFlagData('qa_autostart_feature').value
    },
    userGroupAttrs() {
      return {
        name: 'user_group',
        modalTitle: `${this.$t('global.form.select')} ${this.$t('global.field.userGroup')}`,
        trackBy: 'id',
        tableFields: [
          {
            label: this.$t('global.field.userGroup'),
            key: 'name',
            isSortable: false,
          },
          {
            label: this.$t('global.field.vehicleGroup'),
            key: 'vgroups',
            isSortable: false,
            formatter: (value) => {
              return value?.length != null
                ? value.length === 0
                  ? this.$t('global.text.allVehicleGroup')
                  : value.length
                : '-'
            },
          },
        ],
        listGetter: async function (opts) {
          let result = {}
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_USER_GROUP,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          const ugroups = res.data.ugroups.ugroups

          result = {
            metadata: {
              total_count: ugroups.pagination.total,
              page: ugroups.pagination.page,
            },
            data: {
              paginated_result: ugroups.data,
            },
          }

          return result
        },
        isMandatory: true,
      }
    },
    hasTmsUserGroupAccess() {
      return this.hasAccessAll([
        'tms-user-group:create',
        'tms-user-group:delete',
        'tms-user-group:read',
        'tms-user-group:update',
      ])
    },
    hasAutoEndedQuickAssignment() {
      const data = this.getFeatureFlagValue('customFeature.UnfinishedQA')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    automationBannerItems() {
      const isManualEnded =
        ['MANUAL'].includes(this.showResponse?.ended_by) && this.showResponse?.auto_end_type

      const banner = [
        {
          label: this.isAutoStart
            ? this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.autoRun')
            : this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.run'),
          value: this.getAutomationRunBannerText(),
        },
        ...(!isManualEnded
          ? [
              {
                label:
                  ['ENDED'].includes(this.status) &&
                  ['ALL_VISITED', 'LAST_POINT', 'EXPIRED_DURATION'].includes(
                    this.showResponse?.ended_by
                  )
                    ? this.$t(
                        'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.autoComplete'
                      )
                    : this.$t(
                        'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.complete'
                      ),
                value: this.getAutomationCompleteBannerText(),
              },
            ]
          : []),
        {
          label: this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.activeLimit'),
          value: this.getAutomationActiveLimitBannerText(),
        },
      ].filter((item) => item.value)

      return banner
    },
    isStartPoint() {
      const points = this.listPointRes?.points || []
      return points[0]?.type === 'START'
    },
    isEndPoint() {
      const points = this.listPointRes?.points || []
      return points[points.length - 1]?.type === 'END'
    },
    isAutoStart() {
      return this.showResponse?.started_by === 'AUTOMATION'
    },
  },

  watch: {
    '$route.params.id': {
      async handler(newId) {
        if (newId) {
          this.isLoading = true
          await this.showData()
          this.isLoading = false
        }
      },
      immediate: true,
    },
    shouldShowAdditionalInformation: {
      immediate: true,
      handler(val) {
        if (!val && this.activeTab === 'ADDITIONAL-INFORMATION') {
          this.activeTab = 'MAIN-INFORMATION'
        }
      },
    },
  },
  async created() {
    if (this.$route.params.id === undefined) return

    await this.showData()
    await this.$nextTick()

    if (this.status === 'STARTED') {
      await this.handleSetInterval()
    }

    if (this.$route.query?.duplicated === 'true') {
      this.$mcToast.success(successMsg['duplicate-qa/one']())
    }

    await this.$nextTick()
    this.isLoading = false
  },

  beforeMount() {
    EventBus.$on('update-view-detail-section', this.handleShowDetail)
    EventBus.$on('refetch-fleet-task-instant', this.showData)
    EventBus.$on('user-typing-started', this.handleUserTyping)
  },
  beforeDestroy() {
    this.clearTimeInterval()
    window.removeEventListener('focus', this.fetchOnTabFocus)
    window.removeEventListener('blur', this.clearTimeInterval)

    EventBus.$off('update-view-detail-section', this.handleShowDetail)
    EventBus.$off('refetch-fleet-task-instant', this.showData)
    EventBus.$off('user-typing-started', this.handleUserTyping)

    this.$store.dispatch('fleetTaskInstant/resetAllState')
  },

  methods: {
    formatIndexDate,
    async showData(type = 'init', call = null, opts = {}) {
      const res = await FleetTaskInstant.show(this.$route.params.id)

      if (res?.status === 403 || res?.status === 404) {
        const data = {
          hasButton: true,
        }
        localStorage.setItem(res.status, JSON.stringify(data))
        this.$router.push({
          name: res.status,
        })
        return
      }

      if (res.errors) {
        const adjustedErrors = { ...res.errors }

        adjustedErrors.issues = (res.errors.issues || []).map((issue) => ({
          ...issue,
          source: 'detail',
        }))

        this.$store.dispatch('fleetTaskInstant/setErrorResponse', adjustedErrors)
      }

      this.$store.dispatch('fleetTaskInstant/setShowResponse', res.data)
      await this.$nextTick()

      EventBus.$emit('get-points', type)
      if (call === 'update-map') {
        EventBus.$emit('update-map')
      }

      if (res.data.status.raw_type === 'STARTED') {
        if (opts?.type == 'custom-field') {
          EventBus.$emit('update-form-values', { ...opts })
        }
        return
      }

      EventBus.$emit('update-form-values', { ...opts })

      this.clearTimeInterval()
      window.removeEventListener('focus', this.fetchOnTabFocus)
      window.removeEventListener('blur', this.clearTimeInterval)
    },

    clearTimeInterval() {
      clearInterval(this.timeInterval)
      this.timeInterval = null
    },

    handleUserTyping() {
      clearTimeout(this.typingTimeout)
      this.isUserTyping = true

      this.typingTimeout = setTimeout(() => {
        this.isUserTyping = false
      }, 1000)
    },

    fetchDataPeriodically() {
      clearInterval(this.timeInterval)

      this.timeInterval = setInterval(async () => {
        if (!this.isUserTyping) {
          await this.showData('periodic')
        }
      }, 10000)
    },

    fetchOnTabFocus() {
      this.fetchDataPeriodically()
    },

    async handleSetInterval() {
      if (this.showResponse.status.raw_type !== 'STARTED') return

      this.fetchDataPeriodically()

      window.addEventListener('focus', this.fetchOnTabFocus)
      window.addEventListener('blur', this.clearTimeInterval)
    },

    handleBack() {
      let params = {}
      let routeName = this.$route.query?.navsource || 'Fleet Task'

      if (routeName === 'Liveview 2') {
        redirectLiveview2()
        return
      }

      if (routeName === 'Fleet Task Instant Create') {
        routeName = 'Fleet Task'
      }

      const query = {
        ...this.$route.query,
      }

      if (routeName === 'Fleet Task') {
        query.type = 'instant'
      }

      if (query.navsourceparam) {
        params = JSON.parse(query.navsourceparam)
      }

      if (query.duplicated) {
        delete query.duplicated
      }

      delete query.navsource
      delete query.navsourceparam

      this.$router.push({ name: routeName, params, query })
    },

    getAutomationRunText() {
      if (!this.showResponse?.auto_start_type) {
        return ''
      }

      if (['STARTED', 'ENDED'].includes(this.status) && !this.isAutoStart) {
        return ''
      } else if (['STARTED', 'ENDED'].includes(this.status) && this.isAutoStart) {
        return (
          this.formatIndexDate(this.showResponse?.actual_departure_on).date +
          ' ' +
          this.formatIndexDate(this.showResponse?.actual_departure_on).time
        )
      }

      return this.isStartPoint
        ? this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenDepartingFromStartingPoint')
        : this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtStartingPoint')
    },

    getAutomationCompleteText() {
      if (!this.showResponse?.auto_end_type) {
        return ''
      }

      if (['ENDED'].includes(this.status)) {
        return (
          this.formatIndexDate(this.showResponse?.actual_arrival_on).date +
          ' ' +
          this.formatIndexDate(this.showResponse?.actual_arrival_on).time
        )
      }

      if (['ALL_VISITED'].includes(this.showResponse?.auto_end_type) && !this.isEndPoint) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenAllLocationsVisited')
      } else if (['ALL_VISITED'].includes(this.showResponse?.auto_end_type) && this.isEndPoint) {
        return this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtEndingPointAndAllLocationsVisited'
        )
      }
      if (['LAST_POINT'].includes(this.showResponse?.auto_end_type) && !this.isEndPoint) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenLeavingLastLocation')
      } else if (['LAST_POINT'].includes(this.showResponse?.auto_end_type) && this.isEndPoint) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtEndingPoint')
      }
    },

    getAutomationActiveLimitText() {
      if (!this.showResponse?.auto_end_by_duration || ['ENDED'].includes(this.status)) {
        return ''
      }

      if (['STARTED'].includes(this.status) && this.showResponse?.auto_end_duration_remaining) {
        return this.formatAutomationDurationText(
          this.showResponse?.auto_end_duration_remaining,
          true
        )
      }

      return this.formatAutomationDurationText(this.showResponse?.auto_end_by_duration)
    },

    getAutomationRunBannerText() {
      return this.stripAutomationLeadingWord(
        this.getAutomationRunText(),
        this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.run')
      )
    },

    getAutomationCompleteBannerText() {
      return this.stripAutomationLeadingWord(
        this.getAutomationCompleteText(),
        this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.complete')
      )
    },

    getAutomationActiveLimitBannerText() {
      return this.getAutomationActiveLimitText()
    },

    stripAutomationLeadingWord(text, leadingWord) {
      if (typeof text !== 'string') {
        return ''
      }

      const trimmedText = text.trim()
      const prefix = `${leadingWord} `

      if (trimmedText.toLowerCase().startsWith(prefix.toLowerCase())) {
        return trimmedText.slice(prefix.length).trimStart()
      }

      return trimmedText
    },

    formatAutomationDurationText(duration, isRemaining = false) {
      const parsedDuration = this.parseAutomationDuration(duration)
      const formattedValue = this.buildAutomationDurationLabel(parsedDuration)

      if (!formattedValue) {
        return ''
      }

      if (isRemaining) {
        return this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.remainingValue',
          { value: formattedValue }
        )
      }

      return formattedValue
    },

    buildAutomationDurationLabel({ days = 0, hours = 0, minutes = 0 }) {
      if (days > 0) {
        const labels = [this.translateAutomationDurationUnit('day', days)]

        if (hours > 0) {
          labels.push(this.translateAutomationDurationUnit('hour', hours))
        }

        return labels.join(' ')
      }

      if (hours > 0) {
        return this.translateAutomationDurationUnit('hour', hours)
      }

      return this.translateAutomationDurationUnit('minute', minutes)
    },

    translateAutomationDurationUnit(unit, value) {
      return this.$t(`fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.${unit}`, {
        value,
      })
    },

    parseAutomationDuration(duration) {
      if (typeof duration !== 'string') {
        return { days: 7, hours: 0, minutes: 0 }
      }

      const matched = duration.match(
        /^P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/
      )

      if (!matched) {
        return { days: 7, hours: 0, minutes: 0 }
      }

      const weeks = Number(matched[1] || 0)
      const days = Number(matched[2] || 0) + weeks * 7
      const hours = Number(matched[3] || 0)
      const minutes = Number(matched[4] || 0)
      const seconds = Number(matched[5] || 0)
      let normalizedDays = days
      let normalizedHours = hours
      let normalizedMinutes = minutes

      if (seconds > 0) {
        normalizedMinutes += 1
      }

      if (normalizedMinutes >= 60) {
        normalizedHours += Math.floor(normalizedMinutes / 60)
        normalizedMinutes %= 60
      }

      if (normalizedHours >= 24) {
        normalizedDays += Math.floor(normalizedHours / 24)
        normalizedHours %= 24
      }

      if (normalizedDays > 0) {
        if (normalizedMinutes > 0) {
          normalizedHours += 1
          normalizedMinutes = 0
        }

        if (normalizedHours >= 24) {
          normalizedDays += Math.floor(normalizedHours / 24)
          normalizedHours %= 24
        }

        return {
          days: normalizedDays,
          hours: normalizedHours,
          minutes: 0,
        }
      }

      if (normalizedHours > 0) {
        if (normalizedMinutes > 0) {
          normalizedHours += 1
          normalizedMinutes = 0
        }

        if (normalizedHours >= 24) {
          return { days: 1, hours: 0, minutes: 0 }
        }

        return {
          days: 0,
          hours: normalizedHours,
          minutes: 0,
        }
      }

      if (normalizedMinutes >= 55) {
        return { days: 0, hours: 1, minutes: 0 }
      }

      return {
        days: 0,
        hours: 0,
        minutes: normalizedMinutes,
      }
    },

    getShowResponseQaConfiguration() {
      const hasShowResponseConfiguration =
        this.showResponse?.auto_end_type != null || this.showResponse?.auto_end_by_duration != null

      if (!hasShowResponseConfiguration) {
        return null
      }

      return {
        auto_end_type: this.showResponse?.auto_end_type ?? null,
        auto_end_by_duration: this.showResponse?.auto_end_by_duration ?? null,
      }
    },

    getQaConfigurationFromLocalStorage() {
      try {
        const storedConfiguration = localStorage.getItem('qaConfiguration')

        if (!storedConfiguration) {
          return null
        }

        const parsedConfiguration = JSON.parse(storedConfiguration)
        const hasQaConfigurationShape =
          parsedConfiguration &&
          typeof parsedConfiguration === 'object' &&
          (parsedConfiguration.auto_end_type != null ||
            parsedConfiguration.auto_end_by_duration != null)

        if (!hasQaConfigurationShape) {
          return null
        }

        return {
          auto_end_type: parsedConfiguration.auto_end_type ?? null,
          auto_end_by_duration: parsedConfiguration.auto_end_by_duration ?? null,
        }
      } catch (error) {
        return null
      }
    },

    async getTransitionQaConfiguration() {
      const showResponseConfiguration = this.getShowResponseQaConfiguration()

      if (showResponseConfiguration) {
        return showResponseConfiguration
      }

      const qaConfiguration = this.getQaConfigurationFromLocalStorage()

      if (qaConfiguration) {
        return qaConfiguration
      }

      return null
    },

    /**
     * Transition Status Functional
     */
    async handleTransitionStatus(status, isConfirmAction = false, event = null, data = {}) {
      if (event) {
        event.target.blur()
      }
      this.isLoadingTransition = true
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const { valid, message, errors } = this.checkValidation(status)

      if (!valid) {
        let error = errorMsg[message]
        if (typeof errorMsg[message] === 'function') {
          error = errorMsg[message]()
        }
        this.$mcToast.error(error)
        this.isLoadingTransition = false
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        this.$store.dispatch('fleetTaskInstant/setErrorResponse', errors)

        return
      }

      if (['SCHEDULED', 'STARTED', 'ENDED'].includes(status) && !isConfirmAction) {
        const { isNeedConfirm, type } = this.isNeedConfirmation(status)

        if (isNeedConfirm) {
          this.modalConfirmType = type
          this.pendingTransitionStatus = status
          this.isLoadingTransition = false
          this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
          await this.$nextTick()

          this.$bvModal.show('mc-modal-confirm')
          return
        }
      }

      if (status === 'CANCELED' && !isConfirmAction) {
        this.$refs['mc-modal-canceled'].show()
        this.isLoadingTransition = false
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        return
      }

      const dataReq = {
        status,
      }

      if (data?.additionalPayload) {
        dataReq.fields = data.additionalPayload.fields
      }

      if (this.hasAutoEndedQuickAssignment) {
        const qaConfiguration = await this.getTransitionQaConfiguration()

        if (qaConfiguration) {
          dataReq.fields = {
            ...(dataReq.fields || {}),
            ...qaConfiguration,
          }
        }
      }

      const res = await FleetTaskInstant.transition(this.$route.params.id, dataReq)

      if (status === 'CANCELED') {
        this.$refs['mc-modal-canceled'].hide()
      }

      if (res.errors || res.errors_v2) {
        let errorsData = this.processErrors(res.errors, res.errors_v2)
        const adjustedErrors = {
          ...errorsData,
          source: 'transition',
        }

        this.$store.dispatch('fleetTaskInstant/setErrorResponse', adjustedErrors)
        this.toastTransitionError(errorsData)
        this.isLoadingTransition = false
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)

        return
      }

      this.$mcToast.success(successMsg[`transition-${status.toLowerCase()}`]())
      this.isLoadingTransition = false
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)

      this.$router.push({ name: 'Fleet Task', query: { type: 'instant' } })
    },

    processErrors(errors, errors_v2) {
      const processedErrors = {}

      for (const [key, value] of Object.entries(errors)) {
        if (value[0] === '' && errors_v2 && errors_v2.issues) {
          const matchingIssue = errors_v2.issues.find((issue) => issue.path.join('.') === key)
          if (matchingIssue) {
            processedErrors[key] = [matchingIssue.code]
          } else {
            processedErrors[key] = value
          }
        } else {
          processedErrors[key] = value
        }
      }

      return processedErrors
    },

    handleCancelConfirm() {
      this.modalConfirmType = null
      this.pendingTransitionStatus = null
    },

    handleCancel(isNeedConfirm = true, event = null) {
      if (event) {
        event.target.blur()
      }

      if (isNeedConfirm) {
        this.isShowCancelModal = true
        return
      }

      this.handleBack()
    },

    async handleDelete() {
      this.isShowDeleteModal = false
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.deleteMany({ ids: [this.showResponse.id] })

      if (res.errors || res.failed_count > 0) {
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        this.$mcToast.error(errorMsg['delete-qa/detail']())
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: `<span>${successMsg['delete-qa/detail'](this.showResponse.number)}</span>`,
            },
          ],
        },
      })
      this.$router.push({ name: 'Fleet Task', query: { type: 'instant' } })
    },

    checkValidation(status) {
      const returnObj = { valid: true }

      // no need check validation if status is 'ENDED'
      if (status === 'ENDED') {
        return returnObj
      }

      // required to have minimal one point in all transition status
      if (this.listPointRes?.points.length === 0) {
        const errors = {
          points: [
            {
              address: ['required'],
            },
          ],
        }

        return { valid: false, message: 'min-one-point', errors }
      }

      if (status === 'DRAFT') {
        return returnObj
      }

      // required to have vehicle in all transition status except 'DRAFT'
      if (this.showResponse.vehicle.id === null) {
        return { valid: false, message: 'required-vehicle', errors: { vehicle_id: ['required'] } }
      }

      return returnObj
    },

    /**
     * Check if transition need confirmation modal
     * @param {string} status: transition status
     * @return {object}
     * - isNeedConfirm {boolean}: TRUE if it need confirmation modal, FALSE if it isn't
     * - type {string}: type of confirm modal
     */
    isNeedConfirmation(status) {
      const returnObj = { isNeedConfirm: false, type: null }

      // if it's finishing fleet task, need confirmation modal
      if (status === 'ENDED') {
        returnObj.isNeedConfirm = true
        returnObj.type = 'finish-fleet-task'

        return returnObj
      }

      // if all target time exist, no confirmation modal
      if (!this.listPointRes.is_target_time_exists) {
        returnObj.isNeedConfirm = true
        returnObj.type =
          status === 'SCHEDULED' ? 'scheduled-target-time-exist' : 'started-target-time-exist'

        return returnObj
      }

      // if actual start time < scheduled time, no confirmation modal
      if (status === 'STARTED' && this.isStartLate()) {
        returnObj.isNeedConfirm = true
        returnObj.type = 'late-start-time'
      }

      return returnObj
    },

    /**
     * Check if actual start time is more than scheduled time
     * if actual > scheduled, return true
     * @return {boolean}
     */
    isStartLate() {
      const actualTime = new Date().getTime()
      let scheduledTime
      const firstPoint = this.listPointRes.points[0]

      if (firstPoint.type === 'START') {
        scheduledTime = new Date(firstPoint.target_time_departure).getTime()
      } else {
        scheduledTime = new Date(firstPoint.target_time_arrival).getTime()
      }

      return actualTime > scheduledTime
    },

    toastTransitionError(errors) {
      for (const [key, error] of Object.entries(errors)) {
        let message = ''

        if (error[0].includes('already registered')) {
          message = errorMsg[`${key}/already-registered`]
        } else if (error[0].includes('Point geolocation overlap with point')) {
          message = errorMsg[`address/point-overlap`]
        } else if (error[0].includes('value not found') || error[0].includes('NOT_FOUND')) {
          let changedKey = key
          if (key.includes('points')) {
            changedKey = 'address'
          } else if (key.includes('driver')) {
            changedKey = 'driver_id'
          } else if (key.includes('vehicle')) {
            changedKey = 'vehicle_id'
          }

          message = errorMsg[`${changedKey}/value-not-found`]
        } else if (
          error[0].includes('user group is required') ||
          error[0].includes('vehicle group invalid')
        ) {
          message = errorMsg['transition/default']
        }

        if (typeof message === 'function') {
          message = message()
        }

        if (message === '' || message === undefined) continue

        this.$mcToast.error(message || errorMsg['default']())
        return
      }
    },
    async fetchHistoryListData() {
      const res = await FleetTaskInstant.getListHistory(this.$route.params.id)
      let historyData = res.data
      historyData = historyData.map((data) => {
        const date = new Date(data.created_on)

        const options = { day: '2-digit', month: 'short', year: 'numeric' }
        const formattedDate = date.toLocaleDateString('id-ID', options)

        data.date = formattedDate
        return data
      })
      historyData = Object.groupBy(historyData, ({ date }) => date)
      this.historyKeys = Object.keys(historyData)
      historyData = Object.keys(historyData).map((date) => ({
        [date]: historyData[date],
      }))
      this.historyData = historyData
    },

    async handleFleetTaskInstantHistory() {
      this.openHistory = true
      await this.fetchHistoryListData()
    },

    shareTrackBtnHandler(event) {
      event.target.blur()
      this.$bvModal.show('share-track-modal')
    },

    resetUserGroup() {
      this.skipValidationUserGroup = false
      this.userGroupContext.model = {}
    },

    async handleDuplicate() {
      if (!this.skipValidationUserGroup) {
        if (this.hasTmsUserGroupAccess && this.isSuperUser()) {
          this.$refs['mc-modal-user-group'].$refs['value-help-modal-ref'].show()
          return
        }
      }

      let data = {}
      if (this.hasTmsUserGroupAccess && this.isSuperUser()) {
        data = {
          user_group_id: Number(this.userGroupContext.model),
        }
      }

      const res = await FleetTaskInstant.duplicate(this.$route.params.id, data)

      if (res?.errors_v2) {
        let errorMessage = errorMsg['default']

        if (res?.errors_v2?.issues.length > 0) {
          let errCount = 0
          for (const err of res.errors_v2.issues) {
            if (
              ['FLEET_TASK.DRIVER_USER_GROUP_INVALID', 'FLEET_TASK.VEHICLE_GROUP_INVALID'].includes(
                err.code
              )
            ) {
              const key = err.path?.[0]
              if (['driver', 'vehicle'].includes(key)) {
                errorMessage = errorMsg[`duplicate-qa/${key}-user-group-invalid`]
                this.$mcToast.error({
                  component: 'McToastDynamicBody',
                  props: {
                    contents: [
                      {
                        html: errorMessage(this.$t(`fleetTaskInstant.text.${key}`)),
                      },
                    ],
                  },
                })
                errCount += 1
              }
            }
          }

          if (errCount == res.errors_v2.issues.length) {
            this.resetUserGroup()
            return
          }
        }

        this.$mcToast.error(errorMessage())
        this.resetUserGroup()
        return
      }

      if (res.errors) {
        this.$mcToast.error(errorMsg['duplicate-qa/default']())
        this.resetUserGroup()
        return
      }

      this.resetUserGroup()

      window
        .open(
          `${window.location.origin}/fleet-task-instant/detail/${res?.data?.new_id}?type=instant&duplicated=true`,
          '_blank'
        )
        .focus()
    },

    async handleExportPdf() {
      window.open(
        process.env.VUE_APP_API_FLEET_PLANNING_URL +
          `/fleet-task-instant/${this.$route.params.id}/pdf`
      )
    },

    async handleEnableAutostart() {
      // Status: STARTED, ENDED, CANCELED
      if (['STARTED', 'ENDED', 'CANCELED'].includes(this.status) || !this.isAutostartEnabled) return

      // Status: TEMPORARY, DRAFT
      if (['TEMPORARY', 'DRAFT'].includes(this.status)) {
        await this.handleTransitionStatus('SCHEDULED', true, null, {
          additionalPayload: {
            fields: {
              auto_start_type: 'GEOFENCE',
            },
          },
        })

        return
      }

      // Status: SCHEDULED
      this.isLoadingTransition = true
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.update(this.$route.params.id, {
        auto_start_type: 'GEOFENCE',
      })

      if (res.errors) {
        let errorsData = this.processErrors(res.errors, res.errors_v2)

        this.$store.dispatch('fleetTaskInstant/setErrorResponse', errorsData)
        this.toastTransitionError(errorsData)
        this.isLoadingTransition = false
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)

        return
      }

      await this.showData('init')

      this.isLoadingTransition = false
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
    },

    async handleDisableAutostart() {
      if (
        ['TEMPORARY', 'DRAFT', 'STARTED', 'ENDED', 'CANCELED'].includes(this.status) ||
        !this.isAutostartEnabled
      )
        return

      // Only happened in SCHEDULED status
      this.isLoadingTransition = true
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.update(this.$route.params.id, {
        auto_start_type: null,
      })

      if (res.errors) {
        this.$mcToast.error(res.errors)
      }

      await this.showData('init')
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: this.$t('fleetTaskInstant.toast.autostart.manual', {
                foNumber: this.showResponse.number,
              }),
            },
          ],
        },
      })

      this.isLoadingTransition = false
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
    },

    async handleSaveAutomationConfiguration({ payload, onSuccess, onError }) {
      try {
        const res = await FleetTaskInstant.update(this.$route.params.id, payload)

        if (res?.errors) {
          onError?.()
          return
        }

        await this.showData('init')
        this.$mcToast.success(
          this.$t('fleetTaskInstant.autoEndedQuickAssigment.message.automationSettingsSaved')
        )
        onSuccess?.()
      } catch (error) {
        onError?.()
      }
    },

    handleUserGroupSelect(data) {
      this.userGroupContext.model = data
      this.skipValidationUserGroup = true

      this.$nextTick(() => {
        this.handleDuplicate()
      })
    },

    handleChangeVehicleDriver() {
      if (this.hasTmsUserGroupAccess && this.showResponse?.user_group?.id == null) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: this.$t('fleetTaskInstant.toast.updateVehicleDriver.noUserGroup'),
              },
            ],
          },
        })
        return
      }
      this.$refs['update-vehicle-driver-modal-ref'].HandleOpen()
    },
    handleSettingAction() {
      this.$bvModal.show('automation-configuration-modal')
    },
  },
}
</script>

<style lang="scss" scoped>
.fleet-task-instant-detail {
  &__wrapper {
    position: relative;
    height: 66vh;
  }
}

.task-detail-wrapper {
  height: 66vh;
}

.warning-icon::before {
  font-size: 16px;
  color: $warning;
}

:deep(.mc-fti-banner) {
  width: 100%;
  height: 72px;
  background-color: #fff3e6;
  color: #ff8a00;
  font-size: 13px;
  padding: 0px 20px;
  margin-top: -24px;

  display: flex;
  align-items: center;
  gap: 40px;

  .mc-fti-banner__text {
    height: fit-content;
  }

  .mc-fti-banner__icon:before {
    color: #ff8a00;
    font-size: 32px;
  }
}

.mc-fti-banner + .mc-list-header {
  margin-top: 60px;
}

@keyframes pulseActivateBtn {
  0% {
    border: 2px solid transparent;
    background-color: $green-400;
  }
  50% {
    border: 2px solid $green-200;
    background-color: $green;
  }
  100% {
    border: 2px solid transparent;
    background-color: $green-400;
  }
}

@keyframes pulseNonactivateBtn {
  0% {
    border: 2px solid transparent;
  }
  50% {
    border: 2px solid $mep-secondary-200 !important;
    background-color: $mep-secondary !important;
  }
  100% {
    border: 2px solid transparent;
  }
}

.mc-list-header {
  :deep(.autostart-banner) {
    width: 100%;
    padding: 12px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;

    i:before {
      font-size: 20px;
    }

    &.__success {
      background-color: $green-100;

      i:before {
        color: $green;
      }
    }

    &.__success-automation {
      background-color: $green-100;
      color: $teal;
      gap: 18px;
      flex-wrap: nowrap;
      justify-content: space-between;

      i:before {
        color: $green;
      }

      .autostart-banner__title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .autostart-banner__content {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        justify-content: flex-end;
        flex-wrap: wrap;
        margin-left: auto;
        row-gap: 4px;
      }

      .autostart-banner__item {
        display: inline-flex;
        align-items: center;
        color: $teal;
        white-space: normal;

        strong {
          font-weight: 600;
          margin-right: 4px;
        }

        span {
          color: $teal-300;
        }

        &:not(:last-child)::after {
          content: '\2022';
          font-size: 16px;
          font-weight: 900;
          line-height: 1;
          margin: 0 12px;
          color: $teal-300;
        }
      }

      @media (max-width: 1024px) {
        flex-wrap: wrap;

        .autostart-banner__content {
          justify-content: flex-start;
          margin-left: 0;
        }
      }
    }

    &.__error {
      background-color: #fee8e5;

      i:before {
        color: $red;
      }
    }
  }

  .__divider {
    margin: 0 12px;
    width: 1px;
    min-height: 37.6px;
    height: 100%;
    background-color: $teal-100;
  }

  #activate-autostart {
    :deep(.btn) {
      animation: pulseActivateBtn 2s infinite;

      &.disabled {
        animation: none;
      }

      &:hover {
        border: 2px solid $green-200 !important;
        background-color: $green !important;
      }
    }
  }

  #nonactivate-autostart {
    :deep(.btn) {
      background-color: $mep-secondary-400;
      color: $white;
      animation: pulseNonactivateBtn 2s infinite;

      .mc-icon:before {
        color: $white;
      }

      &.disabled {
        animation: none;
      }

      &:hover {
        background-color: $mep-secondary !important;
        border: 2px solid $mep-secondary-200 !important;
      }
    }
  }
}

// TODO: tidy this up since these styles were copied from index Fleet Task
// START TIDY
:deep(.mc-border) {
  width: 2px;
  background: $white;
}

.btn-outline-primary {
  &:hover,
  &:focus,
  &:active {
    & > .mc-icon:before {
      color: #fff;
    }
  }

  & > .mc-icon:before {
    font-size: 16px;
  }
}

.btn-primary {
  & > .mc-icon:before {
    color: #fff;
  }
}

:deep(.__button-group) {
  .mc-button {
    border-radius: 4px 0 0 4px;
    height: 100%;
  }
}
// END TIDY
</style>

<style lang="scss">
@import '@/design/custom/pages/_fleettaskinstant.scss';
</style>
